import { Button, Table, Tag } from '@ph-react-ui/core';
import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePaginatedPaymentSchemes } from '../../hooks/payment-schemes/usePaginatedSchemes';
import { useModal } from '../../hooks/useModal';
import type { PaymentSchemesDto } from '../../models/payment-schemes/payment-schemes.dto';
import { UPDATE_SCHEME_STATUS } from '../../utils/constants/payment-schemes/modals';
import { displayPrice } from '../../utils/displayPrice';
import { PaymentSchemesOrdering } from '../../utils/enums/payment-schemes-ordering.enum';
import { PaymentSchemesStatus } from '../../utils/enums/payment-schemes-status.enum';
import { ActionMenuHorizontal } from '../shared/components/ActionMenuHorizontal';
import { EmptyState } from '../shared/components/EmptyState';
import { Pagination } from '../shared/components/Pagination';
import { FilterSkeleton } from '../shared/components/skeletons/FilterSkeleton';
import { TableSkeleton } from '../shared/components/skeletons/TableSkeleton';
import { ThSortItem } from '../shared/components/tables/ThSortItem';
import { PaymentSchemesFilter } from './PaymentSchemesFilter';

export const PaymentSchemes = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open: openUpdateStatusModal } = useModal(UPDATE_SCHEME_STATUS);
  const [searchParams] = useSearchParams();

  const openCreateSchemePage = () => {
    navigate('/dashboard/payment-schemes/create');
  };

  const {
    data: paymentSchemes,
    isSuccess,
    isPending,
  } = usePaginatedPaymentSchemes(searchParams);

  const onOpenSchemeEditPage = useCallback(
    (scheme: PaymentSchemesDto) => {
      navigate(`/dashboard/payment-schemes/edit/${scheme.id}`);
    },
    [navigate]
  );

  const onOpenUpdateStatusModal = useCallback(
    (scheme: PaymentSchemesDto) => {
      openUpdateStatusModal({
        schemeId: scheme.id,
      });
    },
    [openUpdateStatusModal]
  );

  const headers = [
    <ThSortItem
      title={t('PAYMENT_SCHEMES.TABLE.HEADERS.NAME')}
      ordering={PaymentSchemesOrdering.Name}
    ></ThSortItem>,
    <ThSortItem
      title={t('PAYMENT_SCHEMES.TABLE.HEADERS.CREATED_ON')}
      ordering={PaymentSchemesOrdering.CreatedAt}
    ></ThSortItem>,
    t('PAYMENT_SCHEMES.TABLE.HEADERS.INSTALLMENTS'),
    t('PAYMENT_SCHEMES.TABLE.HEADERS.YEARLY_PRICE'),
    t('PAYMENT_SCHEMES.TABLE.HEADERS.STATUS'),
    t('PAYMENT_SCHEMES.TABLE.HEADERS.ACTION'),
  ];

  const tableChildren = useMemo(() => {
    return (
      paymentSchemes?.result.map((scheme) => {
        const statusActionTranslation = scheme.status ? 'ACTIVE' : 'INACTIVE';

        return [
          scheme.name,
          scheme.createdAt
            ? format(new Date(scheme.createdAt), 'dd.MM.yyyy')
            : '',
          scheme.templateSchemeEntries.map((e) => `${e.amount}%`).join(', '),
          displayPrice(
            scheme.yearlyPrice,
            t('PAYMENTS.LABELS.BGN'),
            t('PAYMENTS.LABELS.EUR'),
            scheme.currency
          ),
          scheme.status === PaymentSchemesStatus.Active ? (
            <Tag type="success">{t('PAYMENT_SCHEMES.TABLE.STATUS.ACTIVE')}</Tag>
          ) : (
            <Tag type="default">
              {t('PAYMENT_SCHEMES.TABLE.STATUS.INACTIVE')}
            </Tag>
          ),
          <ActionMenuHorizontal
            options={[
              {
                label: t('ACTIONS.EDIT'),
                onClick: () => onOpenSchemeEditPage(scheme),
              },
              {
                label: t(
                  `PAYMENT_SCHEMES.ACTIONS.SET_${statusActionTranslation}_STATUS`
                ),
                onClick: () => onOpenUpdateStatusModal(scheme),
                hidden: !scheme.canEdit,
              },
            ]}
          />,
        ];
      }) ?? []
    );
  }, [paymentSchemes, t, onOpenSchemeEditPage, onOpenUpdateStatusModal]);

  return (
    <div className="payment-schemes">
      <section className="payment-schemes__actions">
        <Button variant="outlined" onClick={openCreateSchemePage}>
          {t('PAYMENT_SCHEMES.ACTIONS.CREATE_SCHEME')}
        </Button>
      </section>
      <form className="payment-schemes__filters">
        {isPending ? <FilterSkeleton /> : <PaymentSchemesFilter />}
      </form>

      {isPending && <TableSkeleton />}

      {isSuccess &&
        paymentSchemes &&
        (paymentSchemes.totalCount > 0 ? (
          <>
            <Table headers={headers} rows={tableChildren} />
            <div className="payment-schemes__pagination p-8 display-flex-row justify-content-center items-center">
              <Pagination data={paymentSchemes} />
            </div>
          </>
        ) : (
          <EmptyState />
        ))}
    </div>
  );
};
